/** Small shared UI primitives: avatar, menus, dialogs, toggles, empty states. */
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { avatarColor, faviconUrl, initials } from "../util";
import { Icon, type IconName } from "./icons";
import { t } from "../i18n";

/* --------------------------------- hooks --------------------------------- */

/** Reactive `matchMedia`. */
export function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = (): void => setMatch(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return match;
}

export const DESKTOP = "(min-width: 768px)";

/* --------------------------------- avatar -------------------------------- */

/** Domains whose favicon is a generic webmail logo — initials read better. */
const GENERIC_DOMAINS = /(^|\.)(gmail|googlemail|outlook|hotmail|live|yahoo|icloud|me|proton|protonmail|aol|gmx)\.[a-z.]+$/i;

export function Avatar({
  address,
  label,
  size = 36,
}: {
  address: string;
  label: string;
  size?: number;
}): JSX.Element {
  // Initials always render; the sender's favicon fades in on top only once it
  // has actually loaded, so slow or missing icons never leave an empty circle.
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const domain = address.split("@")[1] ?? "";
  const url = GENERIC_DOMAINS.test(domain) || failed ? null : faviconUrl(address);
  const style = `width:${size}px;height:${size}px`;

  return (
    <span class="relative inline-flex shrink-0 select-none" style={style} aria-hidden="true">
      <span
        class="flex h-full w-full items-center justify-center rounded-full font-semibold text-on-accent"
        style={`background:${avatarColor(address || label)};font-size:${Math.round(size * 0.36)}px`}
      >
        {initials(label)}
      </span>
      {url && (
        <span
          class={`absolute inset-0 flex items-center justify-center rounded-full border border-line bg-surface transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={url}
            alt=""
            width={Math.round(size * 0.5)}
            height={Math.round(size * 0.5)}
            class="rounded-[3px]"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        </span>
      )}
    </span>
  );
}

export function Spinner({ size = 16, class: cls = "" }: { size?: number; class?: string }): JSX.Element {
  return (
    <span
      class={`inline-block shrink-0 animate-spin rounded-full border-2 border-line-strong border-t-accent ${cls}`}
      style={`width:${size}px;height:${size}px`}
      role="status"
      aria-label={t("common.loading")}
    />
  );
}

/* ---------------------------------- menu --------------------------------- */

interface DropdownProps {
  /** Render the trigger. Spread `props` onto the button. */
  trigger: (props: { onClick: (e: MouseEvent) => void; "aria-expanded": boolean; "aria-haspopup": "menu" }) => JSX.Element;
  children: (close: () => void) => ComponentChildren;
  align?: "start" | "end";
  /** Open upwards (e.g. account menu at the bottom of the sidebar). */
  up?: boolean;
  class?: string;
  menuClass?: string;
}

/**
 * Anchored dropdown menu. Closes on outside click, Escape, or item activation;
 * arrow keys move between `.menu-item`s.
 */
export function Dropdown({ trigger, children, align = "end", up = false, class: cls = "", menuClass = "" }: DropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = (): void => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    // Focus the first item for keyboard users.
    requestAnimationFrame(() => menuRef.current?.querySelector<HTMLElement>(".menu-item:not(:disabled)")?.focus());
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function onKeyDown(e: KeyboardEvent): void {
    if (!open) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
      (ref.current?.querySelector("[aria-haspopup]") as HTMLElement | null)?.focus();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      const items = [...(menuRef.current?.querySelectorAll<HTMLElement>(".menu-item:not(:disabled)") ?? [])];
      const i = items.indexOf(document.activeElement as HTMLElement);
      const next = items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length];
      next?.focus();
    }
  }

  return (
    <div ref={ref} class={/(absolute|fixed)/.test(cls) ? cls : `relative ${cls}`} onKeyDown={onKeyDown}>
      {trigger({
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          setOpen((o) => !o);
        },
        "aria-expanded": open,
        "aria-haspopup": "menu",
      })}
      {open && (
        <div
          ref={menuRef}
          role="menu"
          class={`menu animate-fade absolute z-40 ${align === "end" ? "right-0" : "left-0"} ${up ? "bottom-full mb-1.5" : "top-full mt-1.5"} ${menuClass}`}
          onClick={(e) => {
            // Any activated item closes the menu (inputs inside don't).
            if ((e.target as HTMLElement).closest(".menu-item")) close();
          }}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}

export function MenuItem({
  icon,
  label,
  onSelect,
  hint,
  danger,
  disabled,
  active,
}: {
  icon?: IconName;
  label: string;
  onSelect: () => void;
  hint?: string;
  danger?: boolean;
  disabled?: boolean;
  active?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      class={`menu-item ${danger ? "!text-danger" : ""}`}
      data-active={active ? "true" : undefined}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {icon && <Icon name={icon} size={16} class="shrink-0 opacity-80" />}
      <span class="flex-1 truncate">{label}</span>
      {active && <Icon name="check" size={14} class="text-accent-fg" />}
      {hint && <span class="kbd">{hint}</span>}
    </button>
  );
}

/* --------------------------------- dialog -------------------------------- */

/**
 * Modal dialog: scrim, Escape / outside click to close, initial focus inside,
 * and Tab kept within the panel.
 */
export function Dialog({
  onClose,
  label,
  children,
  class: cls = "max-w-md",
  align = "center",
}: {
  onClose: () => void;
  label: string;
  children: ComponentChildren;
  class?: string;
  align?: "center" | "top";
}): JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  const restore = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    restore.current = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>("[autofocus], input, textarea, select, button:not([data-close])");
    (first ?? panel.current)?.focus();
    return () => restore.current?.focus?.();
  }, []);

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab" || !panel.current) return;
    const items = [
      ...panel.current.querySelectorAll<HTMLElement>("a[href], button:not(:disabled), input, textarea, select, [tabindex='0']"),
    ];
    if (!items.length) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      class={`animate-fade fixed inset-0 z-[60] flex justify-center bg-scrim p-4 ${align === "top" ? "items-start pt-[12vh]" : "items-center"}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        class={`float-panel animate-rise w-full overflow-hidden outline-none ${cls}`}
      >
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({ title, onClose, sub }: { title: string; onClose: () => void; sub?: string }): JSX.Element {
  return (
    <div class="flex items-start gap-3 border-b border-line px-5 py-4">
      <div class="min-w-0 flex-1">
        <h2 class="truncate text-[15px] font-semibold text-fg">{title}</h2>
        {sub && <p class="mt-0.5 text-xs text-fg-3">{sub}</p>}
      </div>
      <button class="icon-btn -mr-1.5 -mt-1" data-close onClick={onClose} aria-label={t("common.close")}>
        <Icon name="x" size={18} />
      </button>
    </div>
  );
}

/* --------------------------------- inputs -------------------------------- */

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      class={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-accent" : "bg-line-strong"
      }`}
    >
      <span
        class={`inline-block h-4 w-4 rounded-full bg-surface shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`}
      />
    </button>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = "md",
}: {
  value: T;
  options: Array<{ value: T; label: string; icon?: IconName }>;
  onChange: (v: T) => void;
  label: string;
  size?: "sm" | "md";
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} class="inline-flex rounded-[7px] bg-raised p-0.5">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            class={`inline-flex items-center gap-1.5 rounded-[5px] font-medium transition-colors ${
              size === "sm" ? "h-6 px-2 text-xs" : "h-7 px-2.5 text-[13px]"
            } ${active ? "bg-surface text-fg shadow-sm" : "text-fg-3 hover:text-fg"}`}
          >
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: IconName;
  title: string;
  body?: string;
  action?: ComponentChildren;
}): JSX.Element {
  return (
    <div class="flex flex-col items-center px-8 py-16 text-center">
      <span class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-raised text-fg-3">
        <Icon name={icon} size={24} />
      </span>
      <h3 class="display-serif text-xl text-fg">{title}</h3>
      {body && <p class="mt-1.5 max-w-[30ch] text-[13px] leading-relaxed text-fg-3">{body}</p>}
      {action && <div class="mt-5">{action}</div>}
    </div>
  );
}

export function Kbd({ children }: { children: ComponentChildren }): JSX.Element {
  return <kbd class="kbd">{children}</kbd>;
}
